import {createContext, useCallback, useContext, useMemo, useState} from 'react';
import PropTypes from 'prop-types';

/**
 * Tracks whether a Contentful `BazaarvoiceReviews` module rendered on the current page.
 * Used by {@link ProductBazaarvoiceFallback} to avoid duplicating the reviews widget when
 * editors have placed the CMS module, while still rendering reviews when they have not.
 *
 * Uses React state (not a ref) so the fallback re-renders to `null` when the CMS module mounts
 * later in the tree (e.g. BV in a Container below Product Detail).
 */
const BazaarvoiceReviewsPresenceContext = createContext(null);

/**
 * @param {object} props
 * @param {string} [props.pageKey] - Remounts / resets presence when the Contentful page changes
 * @param {React.ReactNode} props.children
 */
export function BazaarvoiceReviewsPresenceProvider({pageKey, children}) {
  return (
    <BazaarvoiceReviewsPresenceProviderInner key={pageKey || 'bv-presence'}>
      {children}
    </BazaarvoiceReviewsPresenceProviderInner>
  );
}

BazaarvoiceReviewsPresenceProvider.propTypes = {
  pageKey: PropTypes.string,
  children: PropTypes.node,
};

function BazaarvoiceReviewsPresenceProviderInner({children}) {
  const [present, setPresent] = useState(false);

  const markPresent = useCallback(() => {
    setPresent(true);
  }, []);

  const value = useMemo(
    () => ({markPresent, present}),
    [markPresent, present]
  );

  return (
    <BazaarvoiceReviewsPresenceContext.Provider value={value}>
      {children}
    </BazaarvoiceReviewsPresenceContext.Provider>
  );
}

BazaarvoiceReviewsPresenceProviderInner.propTypes = {
  children: PropTypes.node,
};

/**
 * @returns {{ markPresent: () => void, present: boolean } | null}
 */
export function useBazaarvoiceReviewsPresence() {
  return useContext(BazaarvoiceReviewsPresenceContext);
}
